import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyResourceDc, ExtendedProjectInfoDc, ResourceType } from "@evergis/api";

import { getCopyJobInput } from "../../../../evergis/core/copy/getCopyJobInput";
import { projectInfosWithDeps } from "../../../../resources/projects";
import { getResourceName } from "../../../../utils/resourceName";
import { useAfterCopy } from "../../CopyResource/hooks/useAfterCopy";
import { JobSource, useCopyJobNotifications } from "../../CopyResource/hooks/useCopyJobNotifications";
import { useCopyResources } from "../../CopyResource/hooks/useCopyResources";
import { useSelectedResourcesNames } from "../../CopyResource/hooks/useSelectedResourcesNames";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";
import { BulkCopyJob } from "../../CopyResource/types";
import { FilterCopyTask } from "../../../../evergis/core/tools/createFilterCopyTask";
import { TaskDataType } from "../../../../interfaces/scheduler";

export const useBulkCopyProjectsJob = (copyLayers: boolean, copyTables: boolean): BulkCopyJob => {
  const { t } = useTranslation("dialogs");
  const resourcesToCopy: string[] = useSelectedResourcesNames();
  const { data: resourcesInfo } = projectInfosWithDeps.use({ input: resourcesToCopy, waitInput: true });
  const { copyJob, copyData: jobResult, copyLoading: jobLoading, copyError: jobError } = useCopyResources();
  const [waiting, setWaiting] = useState(false);
  const firstAlias = (resourcesInfo && resourcesInfo.projects[0] && resourcesInfo.projects[0].alias) || "";
  const notificationInfos = useCopyJobNotifications(JobSource.BulkProjects, { alias: firstAlias, jobResult });
  const { setInnerTasks } = useAfterCopy(notificationInfos, { jobResult, jobLoading, jobError });
  const defaultScaleRange = useDefaultScaleRange();

  const onCopy = useCallback(async () => {
    if (!resourcesInfo) {
      return;
    }

    setWaiting(true);

    let innerTasks: FilterCopyTask<TaskDataType.Table>[] = [];
    let innerCopyResources: CopyResourceDc[] = [];

    const projectsInput: CopyResourceDc[] = resourcesInfo.projects
      ? resourcesInfo.projects.map((project: ExtendedProjectInfoDc) => {
          const { alias: projectAlias, name: projectSourceName, content: projectContent } = project;
          const projectItems: string[] = projectContent.items ? projectContent.items.map(item => item.name) : [];
          const projectTargetName: string = getResourceName();
          let copyWith: string[] = null;

          if (copyLayers) {
            const [layerInput, tableInput, tasks] = getCopyJobInput({
              layers: resourcesInfo.deps.layers.filter(item => projectItems.includes(item.name)),
              tables: resourcesInfo.deps.tables,
              copyTables,
              defaultScaleRange,
            });

            innerCopyResources = [...innerCopyResources, ...layerInput, ...tableInput];
            innerTasks = [...innerTasks, ...tasks];
            copyWith = layerInput.map(item => item.copyName);
          }

          return {
            type: ResourceType.Project,
            name: projectSourceName,
            copyName: projectTargetName,
            copyAlias: `${projectAlias} ${t("bulkCopy.copy")}`,
            copyWith,
          };
        })
      : [];

    setInnerTasks(innerTasks);

    await copyJob({
      input: [...projectsInput, ...innerCopyResources],
    });
  }, [resourcesInfo, copyLayers, copyTables, t]);

  return { onCopy, waiting };
};
